// Shared calendar/date + vehicle-category helpers used by CalendarPage and AvailabilityPage.

export function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }
export function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
export function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
export function sameDay(a, b) { return a.toDateString() === b.toDateString(); }
export function dateOnly(s) {
  if (!s) return null;
  const d = new Date(s);
  if (isNaN(d)) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function buildGrid(d) {
  const first = startOfMonth(d), last = endOfMonth(d);
  const days = [];
  for (let i = first.getDay(); i > 0; i--) days.push(new Date(first.getFullYear(), first.getMonth(), 1 - i));
  for (let i = 1; i <= last.getDate(); i++) days.push(new Date(first.getFullYear(), first.getMonth(), i));
  while (days.length % 7 !== 0) { const l = days[days.length - 1]; days.push(new Date(l.getFullYear(), l.getMonth(), l.getDate() + 1)); }
  return days;
}

// Categorize a vehicle into END / Coupes / Sedans / SUVs. Prefer the explicit
// vehicle_class set in Fleet (coupe/sedan/suv); fall back to a model-name guess
// for legacy/unclassified vehicles so the availability matrix still groups them.
// END-flagged vehicles short-circuit into the END bucket (and are excluded from
// the other tabs so they only surface under END).
export function categorize(v) {
  if (v && v.is_end_vehicle) return "END";
  const cls = `${v.vehicle_class || ""}`.toLowerCase().trim();
  if (cls === "coupe" || cls === "coupes") return "Coupes";
  if (cls === "suv" || cls === "suvs") return "SUVs";
  if (cls === "sedan" || cls === "sedans") return "Sedans";
  const m = `${v.model || ""}`.toLowerCase();
  const SUV = ["suv", "wrangler", "range rover", "velar", "macan", "cayenne", "gle", "glb", "glc", "x3", "x4", "x5", "x6", "suburban", "escalade", "santa fe", "atlas", "rvr", "venue", "kicks", "jeep"];
  const COUPE = ["coupe", "boxster", "r8", "corvette", "mustang", "challenger", "m3", "m4", "c43 coupe", "c63", "amg gt", "gt 53", "convertible", "conv"];
  if (SUV.some((k) => m.includes(k))) return "SUVs";
  if (COUPE.some((k) => m.includes(k))) return "Coupes";
  return "Sedans";
}

// "What's available TODAY" — fully-free cars plus cars returning before 9 PM.
// A car counts as returning today if its active booking ends today at ≤ 21:00
// and nothing picks it back up before that cutoff. Each row carries `next` =
// the next pickup within 7 days so you can see the real runway.
// Bookings are matched by object identity, so this works with the sanitized
// /availability payload (which has no booking ids for affiliate logins).
export function computeTodayAvailability(vehicles, bookings) {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  const cutoff21 = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 21, 0, 0, 0);
  const weekAhead = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

  const active = bookings.filter(
    (b) => b.vehicle_id && !["cancelled", "completed", "rejected"].includes(b.status)
  );

  const nextPickup = (mine, after, ignore) => {
    const upcoming = mine
      .filter((b) => b !== ignore)
      .map((b) => ({ b, start: new Date(b.start_datetime) }))
      .filter(({ start }) => start > after && start <= weekAhead)
      .sort((a, b) => a.start - b.start);
    return upcoming.length ? upcoming[0] : null;
  };

  const free = [];
  const returning = [];

  for (const v of vehicles) {
    if (v.status === "retired" || v.status === "maintenance") continue;
    const mine = active.filter((b) => b.vehicle_id === v.id);
    const overlaps = mine.filter((b) => {
      const s = new Date(b.start_datetime);
      const e = b.end_datetime ? new Date(b.end_datetime) : s;
      return s <= todayEnd && e >= todayStart;
    });

    if (overlaps.length === 0) {
      const startsToday = mine.some((b) => {
        const s = new Date(b.start_datetime);
        return s >= todayStart && s <= todayEnd;
      });
      if (!startsToday) free.push({ vehicle: v, next: nextPickup(mine, now, null) });
      continue;
    }

    const latest = overlaps.reduce((a, b) =>
      new Date(a.end_datetime || a.start_datetime) > new Date(b.end_datetime || b.start_datetime) ? a : b
    );
    const endDt = latest.end_datetime ? new Date(latest.end_datetime) : null;
    if (!endDt || endDt < todayStart || endDt > todayEnd || endDt > cutoff21) continue;

    const rebooked = mine.some((b) => {
      if (b === latest) return false;
      const s = new Date(b.start_datetime);
      return s >= endDt && s <= cutoff21;
    });
    if (rebooked) continue;

    returning.push({ vehicle: v, booking: latest, returnAt: endDt, next: nextPickup(mine, endDt, latest) });
  }

  returning.sort((a, b) => a.returnAt - b.returnAt);
  free.sort((a, b) =>
    (a.vehicle.make || "").localeCompare(b.vehicle.make || "")
    || (a.vehicle.model || "").localeCompare(b.vehicle.model || "")
  );
  return { free, returning };
}
